import type { ProcurementPlanDetail } from '@ebalance/proto/bffuser/v1/procurement_plan_model_pb'

import { isJointBg } from '~/pages/ProcurementPlanDetail/models/jointBg'

/**
 * 計画内不整合 (AC-IC-005 / 006) のコマを強調するための、画面側の表示用の束
 * (AC-PPD-024 / 025)。コマ index は 0 始まり (コマ番号 − 1)。
 */
export type InternalInconsistencyView = {
  /** 需給バランス ≠ 0 のコマ index (AC-PPD-024) */
  balanceRows: ReadonlySet<number>
  /** BG 全体 ≠ Σ小売電気事業者別のコマ index → 差 (BG 全体 − Σ小売、kWh) (AC-PPD-025) */
  retailerGaps: ReadonlyMap<number, number>
}

type Params = {
  /** 表示中の版の詳細 */
  detail: Pick<
    ProcurementPlanDetail,
    'internalBalanceMismatchKomaNumbers' | 'internalRetailerDemandMismatches'
  >
  /** 種別「BG」を表示しているか (事業者を選んでいないか) */
  isBgView: boolean
  /** 内訳の小売電気事業者数 (単独 BG = 1、合同 BG = 2 以上) */
  retailerCount: number
}

/**
 * 強調する対象を決める純関数。判定そのものは backend が表示中の版の値で行った結果
 * (一覧・ヘッダーと同じ規則) を受け取り、ここでは出す条件だけを決める:
 *   - 種別「BG」の表示では出す
 *   - 事業者を選んでいる時は、単独 BG (事業者 1 社) だけ出す。合同 BG は代表事業者を含めて出さない
 *     (事業者ごとの値が合わないのは JEPX を代表事業者に寄せるため。AC-PPD-024 / 025 ※)
 * 該当のコマが無ければ undefined (強調も凡例も出さない)。
 */
export function resolveInternalInconsistency({
  detail,
  isBgView,
  retailerCount,
}: Params): InternalInconsistencyView | undefined {
  if (!isBgView && isJointBg(retailerCount)) return undefined
  const balanceRows = new Set(
    detail.internalBalanceMismatchKomaNumbers.map((koma) => koma - 1),
  )
  const retailerGaps = new Map(
    detail.internalRetailerDemandMismatches.map((m) => [
      m.komaNumber - 1,
      Number(m.diffKwh),
    ]),
  )
  if (balanceRows.size === 0 && retailerGaps.size === 0) return undefined
  return { balanceRows, retailerGaps }
}
